'use client';
// Réactifs de contrôle et de calibration — calibrants et contrôles CQI.
//
// ⚠️ L'écran s'appelait « Matériaux reconstitués » jusqu'au 06/10/2026 (RQ) :
// « il y en a qui ne sont pas à reconstituer du tout ». Un nom qui exclut une
// partie de ce qu'il range fait chercher les réactifs prêts à l'emploi
// ailleurs — ou les laisse dehors. La CLÉ d'écran (`mat`) et le SLUG
// (`materiaux`) ne bougent pas : un lien partagé doit continuer d'ouvrir
// l'écran (`lib/adresse.ts`).
//
// Deux collections DISTINCTES en base, et il faut qu'elles le restent : un
// calibrant règle la mesure, un contrôle la vérifie. Mais au poste c'est le même
// geste — on reconstitue un flacon, on note son lot, sa péremption, ce qu'il en
// reste — d'où cet écran commun. La colonne « famille » ne se perd jamais.
//
// Les alertes ALERTENT, elles ne bloquent pas : c'est le technicien qui juge
// s'il utilise un flacon proche de sa péremption. L'outil montre, il ne décide
// pas à sa place.
import React, { useEffect, useMemo, useState } from 'react';
import { fmtDate } from '@/lib/meta';
import { I, card, EmptyState, FilterBar, RefISO } from '@/components/ui';
import {
  ActionsLigne, ArchiverModale, BadgeArchive, FormulaireLigne,
} from '@/components/RegistreEdition';
import type { ChampDef } from '@/components/RegistreEdition';
import { Tableau } from '@/shared/Tableau';
import { H1 } from './commun';
import { Aliquots } from './Aliquots';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { useGed, userName } from '@/lib/store';
type Materiau = {
  documentId: string; ref: string; famille: string; libelle: string;
  discipline: string; equipementCode: string; niveau: string; numeroLot: string;
  peremption: string; dateOuverture: string; dateReconstitution: string;
  codeBoxystock: string; numeroFlacon: string; nombreUnites: string;
  volumeParUnite: string; flaconsRestants: string; personnel: string; remarque: string;
  // Écriture (lot 2, 10/08/2026) — état d'archivage + champs bruts servis pour
  // que la correction n'efface pas ce qu'elle ne réaffiche pas.
  archive: boolean; archiveMotif: string;
  /** « oui » | « non » | '' tel qu'en base, pour le formulaire de correction. */
  reconstitution: string;
  /** La lecture du serveur — l'écran ne réinterprète pas le vide pour son
   *  compte (`seReconstitue`, backend/src/materiaux-echeance.ts). */
  seReconstitue: boolean;
  /** Les trois températures : brutes pour le formulaire, DITES pour l'écran. */
  temperatureAvant: string; temperatureApres: string; temperatureAliquots: string;
  temperatureAvantDite: string; temperatureApresDite: string; temperatureAliquotsDite: string;
  /** L'écart notice ↔ pratique, en clair, ou '' s'il n'y en a pas — ou si
   *  l'une des deux manque, auquel cas on ne sait pas. */
  ecartConservation: string;
  refFournisseur: string; peremptionApresReconstitution: string;
  volumeReconstitution: string; parametre: string; passage: string;
  dateUtilisation: string; cupulesReconstituees: string;
  cupulesUtilisees: string; cupulesRestantes: string;
  joursAvantPeremption: number | null;
  perime: boolean; bientotPerime: boolean; peremptionInconnue: boolean; epuise: boolean;
  // L'ÉCHÉANCE — calculée serveur : la PREMIÈRE de la péremption du lot et de
  // la péremption après reconstitution (RQ, 03/10/2026), avec la borne qui a
  // joué. `perime`, `bientotPerime`, `joursAvantPeremption` la suivent depuis
  // ce jour. Rien n'est recalculé ici : deux écrans qui refont le même calcul
  // finissent par ne plus dire la même chose.
  cleProduit: string;
  causeEcheance: '' | 'lot' | 'stabilite';
  causeLibelle: string;
  /** « périmé : fin du lot », « bientôt périmé : fin de stabilité », ou ''. */
  phraseEcheance: string;
  /** Les dates qui manquent, même quand l'échéance est établie sur l'autre. */
  manquesEcheance: string[];
  // Propriétés de notice d'un flacon de contrôle (03/10/2026), en texte.
  volumeReconstitutionUl: string; stabiliteCongeleJours: string;
  stabiliteDecongeleHeures: string; usageAliquot: string;
  validiteJours: number | null;
  /** Date à laquelle prévenir — proposée par le serveur, jamais imposée. */
  dateNotification?: string;
  dateDepartValidite: string;
  sourceEcheance: string;
  echeanceApresOuverture: string;
  joursAvantEcheance: number | null;
  etatValidite: 'non_parametree' | 'date_absente' | 'depassee' | 'proche' | 'valide';
};
type Compte = {
  total: number; archives: number; calibrants: number; controles: number;
  perimes: number; bientotPerimes: number; peremptionInconnue: number; epuises: number;
  pretsALEmploi: number;
  sansTemperature: number; ecartTemperature: number;
  perimesParCause: { lot: number; stabilite: number };
  bientotPerimesParCause: { lot: number; stabilite: number };
  validiteDepassee: number; validiteProche: number;
  validiteNonParametree: number; validiteDateAbsente: number;
};

/* ── Écriture (lot 2) — champs par famille et rapprochement vue → champs bruts.
 *
 * Le moteur écrit les CHAMPS BRUTS des deux collections (`calibrant` vs
 * `materiau`) ; l'écran affiche une forme normalisée (`libelle`). Le
 * formulaire repart donc des champs bruts, servis exprès par la lecture. */
/* ── AIDE À LA SAISIE (05/10/2026) — les unités et les dates ────────────────
 *
 * Deux saisies se trompaient en SILENCE sur ce formulaire, et c'est ce que
 * l'aide vise :
 *
 *  · les DATES. Le moteur ne lit comme date que `aaaa-mm-jj`
 *    (`materiaux-echeance.ts`) ; « 12/03/2026 » s'enregistre sans broncher et
 *    la ligne ressort « péremption non saisie » — ni périmée ni valide, hors
 *    de toutes les alertes. Elles passent donc par le sélecteur du navigateur
 *    (`type: 'date'`), qui ne peut produire que la bonne forme. Les dates
 *    imprécises de la reprise Excel restent saisissables, et DITES (`ChampDate`).
 *
 *  · les UNITÉS. Trois champs de notice sont des NOMBRES en base, bornés par
 *    `normaliserProprietesFlacon` : l'unité entre parenthèses dans l'étiquette
 *    se lisait avant la saisie et s'oubliait pendant (« 30 j », refusé). Elle
 *    est passée contre le champ (`unite`), avec la borne du moteur dite sous
 *    lui (`aide`) plutôt qu'apprise par un refus. */
/* ── RECONSTITUTION FACULTATIVE (RQ, 06/10/2026) ───────────────────────────
 *
 * « Il y en a qui ne sont pas à reconstituer du tout. » Le champ est posé en
 * TÊTE des deux formulaires, juste après le nom du produit : c'est la question
 * qui commande les suivantes, et la poser au milieu des dates obligerait à
 * remonter corriger ce qu'on vient de saisir.
 *
 * ⚠️ `visibleSi` MASQUE, il n'efface pas (voir `ChampDef`) : basculer un
 * réactif en « prêt à l'emploi » ne perd pas la date de reconstitution déjà
 * saisie, elle réapparaît si l'on revient en arrière.
 *
 * ⚠️ LA PÉREMPTION APRÈS OUVERTURE RESTE RÉCLAMÉE dans les deux cas. Un
 * réactif prêt à l'emploi mais ouvert a bien une stabilité — c'est la même
 * borne, sous un autre mot. Ne pas la masquer « parce qu'il n'y a pas de
 * reconstitution » : on perdrait une alerte de péremption. */
const RECONSTITUTION: ChampDef = {
  k: 'reconstitution', label: 'Ce réactif se reconstitue-t-il ?', type: 'options',
  options: [['oui', 'Oui — à reconstituer'], ['non', 'Non — prêt à l’emploi']],
  aide: 'Propriété du produit, lue sur la notice. Laissé vide, il est tenu pour « à reconstituer » — c’est ce que sont les lignes reprises des classeurs.',
};
/** Le champ n'a de sens que si le produit se reconstitue. Vide = oui. */
const SI_RECONSTITUE = (v: Record<string, string>) => v.reconstitution !== 'non';

/* ── LES TEMPÉRATURES DE CONSERVATION (RQ, 06/10/2026) ─────────────────────
 *
 * ⚠️ TROIS CHAMPS, DEUX NATURES, et c'est l'écart entre eux qui a de la valeur.
 * « Avant » et « après » sont ce que le FABRICANT prescrit, recopié de la
 * notice. « Aliquots » est ce que le LABORATOIRE fait : le Lyphochek prescrit
 * 2 – 8 °C après reconstitution, et le laboratoire congèle à −20 °C sur la
 * stabilité congelé de cette même notice. Les fondre en un seul champ ferait
 * disparaître l'écart entre la prescription et la pratique — précisément ce
 * qu'un évaluateur vient regarder. Vocabulaire et libellés :
 * backend/src/materiaux-conservation.ts. */
const TEMPERATURES: readonly (readonly [string, string])[] = [
  ['ambiante', 'Température ambiante (15 – 25 °C)'],
  ['refrigere', 'Réfrigérateur (2 – 8 °C)'],
  ['congele', 'Congélateur (−20 °C)'],
  ['tres_basse', 'Congélateur basse température (−70 °C ou moins)'],
];
const TEMPERATURE_AVANT: ChampDef = {
  k: 'temperatureAvant', label: 'Conservation avant reconstitution', type: 'options', options: TEMPERATURES,
  aide: 'Le kit fermé, tel que la notice le prescrit. Laissé vide, rien n’est supposé.',
};
const TEMPERATURE_APRES: ChampDef = {
  k: 'temperatureApres', label: 'Conservation après reconstitution ou ouverture', type: 'options', options: TEMPERATURES,
  aide: 'Ce que le FABRICANT prescrit — à ne pas confondre avec le congélateur où partent réellement les aliquots, qui se saisit au geste d’aliquotage.',
};

const CALIBRANT_CHAMPS: readonly ChampDef[] = [
  { k: 'calibrant', label: 'Calibrant', requis: true },
  RECONSTITUTION,
  { k: 'equipementCode', label: 'Équipement (code)' },
  {
    k: 'dateReconstitution', label: 'Date de reconstitution', type: 'date',
    visibleSi: SI_RECONSTITUE,
    aide: 'Le jour du geste, pas celui de la saisie.',
  },
  { k: 'numeroLot', label: 'Numéro de lot' },
  {
    k: 'refFournisseur', label: 'Référence fournisseur',
    aide: 'Celle du catalogue du fournisseur — c’est elle qui rapproche deux boîtes du même produit.',
  },
  {
    k: 'peremption', label: 'Péremption (kit fermé)', type: 'date',
    aide: 'Recopiée de la boîte : c’est le fournisseur qui l’imprime.',
  },
  {
    /* Le libellé porte LES DEUX CAS plutôt que de changer selon la case du
       dessus : un intitulé qui bouge sous les yeux pendant la saisie se relit
       mal, et la borne, elle, est bien la même. */
    k: 'peremptionApresReconstitution', label: 'Péremption après reconstitution ou ouverture', type: 'date',
    aide: 'Fin de stabilité, lue sur la notice — après reconstitution, ou après ouverture pour un prêt à l’emploi. L’échéance retenue est la PREMIÈRE des deux dates, et l’écran dit laquelle.',
  },
  TEMPERATURE_AVANT,
  TEMPERATURE_APRES,
  {
    k: 'volumeReconstitution', label: 'Volume de reconstitution', exemple: '3 mL',
    visibleSi: SI_RECONSTITUE,
    aide: 'Avec son unité : ce champ est du texte, rien n’est converti.',
  },
  { k: 'codeBoxystock', label: 'Code Boxystock' },
  { k: 'numeroFlacon', label: 'Numéro de flacon' },
  { k: 'nombreUnites', label: 'Nombre d’unités' },
  { k: 'flaconsRestants', label: 'Flacons restants' },
  { k: 'parametre', label: 'Paramètre (hémostase)' },
  { k: 'cupulesReconstituees', label: 'Cupules reconstituées', visibleSi: SI_RECONSTITUE },
  { k: 'cupulesUtilisees', label: 'Cupules utilisées' },
  { k: 'cupulesRestantes', label: 'Cupules restantes' },
  { k: 'dateUtilisation', label: 'Date d’utilisation', type: 'date' },
  { k: 'personnel', label: 'Personnel' },
  { k: 'remarque', label: 'Remarque', type: 'long' },
];
const FLACON_CHAMPS: readonly ChampDef[] = [
  { k: 'materiau', label: 'Matériau de contrôle', requis: true },
  RECONSTITUTION,
  {
    k: 'niveau', label: 'Niveau', exemple: '1',
    aide: 'Tel qu’il est écrit sur la boîte : 1 / 2 / 3, ou N / P (normal, pathologique) en hémostase. Sans lui, le flacon ne se rattache à aucune carte.',
  },
  { k: 'discipline', label: 'Discipline' },
  { k: 'passage', label: 'Passage' },
  {
    k: 'dateReconstitution', label: 'Date de reconstitution', type: 'date',
    visibleSi: SI_RECONSTITUE,
    aide: 'Le jour du geste, pas celui de la saisie : c’est d’elle que partent les stabilités ci-dessous.',
  },
  { k: 'dateOuverture', label: 'Date d’ouverture', type: 'date' },
  { k: 'numeroLot', label: 'Numéro de lot' },
  {
    k: 'refFournisseur', label: 'Référence fournisseur',
    aide: 'Celle du catalogue du fournisseur — c’est par elle que les propriétés ci-dessous seront reprises au prochain flacon du même produit.',
  },
  {
    k: 'peremption', label: 'Péremption (kit fermé)', type: 'date',
    aide: 'Recopiée de la boîte : c’est le fournisseur qui l’imprime.',
  },
  /* 27/08/2026 : la date de vie du flacon se saisit ICI, avec sa référence, et
     non plus comme une durée dans le paramétrage. Elle est imprimée sur la
     boîte ou donnée par la notice — on la recopie, on ne la recalcule pas. */
  {
    k: 'peremptionApresReconstitution', label: 'Péremption après reconstitution ou ouverture', type: 'date',
    aide: 'Fin de stabilité, lue sur la notice — après reconstitution, ou après ouverture pour un prêt à l’emploi. L’échéance retenue est la PREMIÈRE des deux dates, et l’écran dit laquelle.',
  },
  { k: 'codeBoxystock', label: 'Code Boxystock' },
  { k: 'numeroFlacon', label: 'Numéro de flacon' },
  {
    k: 'nombreUnites', label: 'Nombre d’unités', exemple: '10',
    aide: 'Combien d’aliquots ce flacon donne.',
  },
  {
    k: 'volumeParUnite', label: 'Volume par unité', exemple: '500 µL',
    aide: 'Le volume d’UN aliquot, avec son unité — à ne pas confondre avec le volume de reconstitution du flacon, juste dessous.',
  },
  /* 03/10/2026 : les propriétés de NOTICE qui datent les aliquots — saisies et
     corrigées avec le flacon, plus au paramétrage. Vides = non renseignées.
     Les bornes des `aide` sont celles de `normaliserProprietesFlacon`
     (backend/src/cqi-aliquots.ts) : si elles bougent là, les redire ici. */
  TEMPERATURE_AVANT,
  TEMPERATURE_APRES,
  {
    /* Saisi AU GESTE d'aliquotage (« Reconstituer un flacon ») ; corrigeable
       ici, par la même route que le reste du flacon — sinon une erreur de
       congélateur serait indéboulonnable. */
    k: 'temperatureAliquots', label: 'Où partent les aliquots', type: 'options', options: TEMPERATURES,
    aide: 'Le congélateur réel, constaté au geste — pas ce que prescrit la notice. L’écart entre les deux est signalé, il n’est pas une faute.',
  },
  {
    k: 'volumeReconstitutionUl', label: 'Volume de reconstitution', unite: 'µL', exemple: '3000',
    visibleSi: SI_RECONSTITUE,
    aide: 'Volume d’eau ajouté au flacon. Nombre positif, 100 000 au plus ; la virgule est admise.',
  },
  {
    k: 'stabiliteCongeleJours', label: 'Stabilité d’un aliquot congelé', unite: 'jours', exemple: '30',
    aide: 'À −20 °C, comptés depuis la reconstitution — ou depuis l’ouverture pour un prêt à l’emploi. Nombre ENTIER de jours, 3 650 au plus.',
  },
  {
    k: 'stabiliteDecongeleHeures', label: 'Stabilité après décongélation', unite: 'heures', exemple: '8',
    aide: 'Comptées depuis le déstockage de l’aliquot. 1 440 au plus (soixante jours) ; la virgule est admise.',
  },
  {
    k: 'usageAliquot', label: 'Usage d’un aliquot', type: 'options',
    options: [['unique', 'unique'], ['multiple', 'multiple (dans sa stabilité)']],
    aide: 'Vide = traité comme un usage unique : le tube servi est consommé.',
  },
  { k: 'personnel', label: 'Personnel' },
  { k: 'remarque', label: 'Remarque', type: 'long' },
];

/** La ligne normalisée, remise dans les champs bruts de sa collection. */
function versBrut(m: Materiau): Record<string, unknown> {
  const commun = {
    documentId: m.documentId, reconstitution: m.reconstitution,
    temperatureAvant: m.temperatureAvant, temperatureApres: m.temperatureApres,
    dateReconstitution: m.dateReconstitution,
    refFournisseur: m.refFournisseur, numeroLot: m.numeroLot, peremption: m.peremption,
    codeBoxystock: m.codeBoxystock, numeroFlacon: m.numeroFlacon,
    nombreUnites: m.nombreUnites, personnel: m.personnel, remarque: m.remarque,
  };
  return m.famille === 'calibrant'
    ? {
      ...commun, calibrant: m.libelle, equipementCode: m.equipementCode,
      peremptionApresReconstitution: m.peremptionApresReconstitution,
      volumeReconstitution: m.volumeReconstitution, flaconsRestants: m.flaconsRestants,
      parametre: m.parametre, cupulesReconstituees: m.cupulesReconstituees,
      cupulesUtilisees: m.cupulesUtilisees, cupulesRestantes: m.cupulesRestantes,
      dateUtilisation: m.dateUtilisation,
    }
    : {
      /* `peremptionApresReconstitution` manquait ici : corriger un flacon de
         contrôle l'aurait EFFACÉE (le formulaire renvoie ce qu'il affiche, et
         affichait vide). Idem pour les propriétés de notice du 03/10. */
      ...commun, materiau: m.libelle, niveau: m.niveau, discipline: m.discipline,
      temperatureAliquots: m.temperatureAliquots,
      passage: m.passage, dateOuverture: m.dateOuverture, volumeParUnite: m.volumeParUnite,
      peremptionApresReconstitution: m.peremptionApresReconstitution,
      volumeReconstitutionUl: m.volumeReconstitutionUl, stabiliteCongeleJours: m.stabiliteCongeleJours,
      stabiliteDecongeleHeures: m.stabiliteDecongeleHeures, usageAliquot: m.usageAliquot,
    };
}

const CHEMINS = { calibrant: 'ged/materiaux/calibrants', controle: 'ged/materiaux/cqi-flacons' } as const;

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12.5 };

/** Une valeur absente s'affiche comme absente — jamais comme une cellule vide
 *  qu'on lirait « rien à signaler ». */
const V = ({ v }: { v: string | null | undefined }) => (
  v && v.trim()
    ? <>{v}</>
    : <span style={{ color: 'var(--text-muted)' }} title="Non renseigné dans la source">—</span>
);

const FAMILLES: Record<string, { label: string; aide: string }> = {
  calibrant: { label: 'Calibrant', aide: 'Règle la mesure de l’appareil.' },
  controle: { label: 'Contrôle CQI', aide: 'Vérifie la mesure — jamais le même matériau que le calibrant.' },
};

/** L'ÉCHÉANCE d'un flacon, dite en toutes lettres, AVEC sa cause.
 *
 *  Depuis le 03/10/2026 (RQ), la première de la péremption du lot et de la
 *  péremption après reconstitution fait foi : « périmé : fin du lot »,
 *  « bientôt périmé : fin de stabilité ». Les DEUX dates restent affichées à
 *  côté, et celle qui manque est dite — une échéance établie sur une seule
 *  borne n'est pas complète.
 *
 *  « Inconnue » est un état À PART : ce n'est ni périmé ni valide, et l'afficher
 *  comme valide ferait passer pour sûr un flacon dont personne ne sait rien. */
function Echeance({ m }: { m: Materiau }) {
  const deux = (
    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
      lot {m.peremption ? fmtDate(m.peremption) : <em>non saisi</em>}
      {' · '}après reconstitution {m.peremptionApresReconstitution ? fmtDate(m.peremptionApresReconstitution) : <em>non saisie</em>}
    </span>
  );
  if (m.peremptionInconnue) {
    return (
      <span title="Ni la péremption du lot ni celle après reconstitution ne sont lisibles : l’échéance ne peut pas être établie. Ce n’est pas « encore valide »."
        style={{ display: 'inline-flex', gap: 8, alignItems: 'baseline', cursor: 'help' }}>
        <span style={{ fontSize: 12.5, fontStyle: 'italic', color: 'var(--amber-600)' }}>péremption non saisie</span>
      </span>
    );
  }
  const j = m.joursAvantPeremption as number;
  const couleur = m.perime ? 'var(--red-600)' : m.bientotPerime ? 'var(--amber-600)' : 'var(--text-body)';
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}
      title={`${m.manquesEcheance.length ? `${m.manquesEcheance.join(' ; ')} — l’échéance repose sur l’autre date seule.` : 'Première des deux dates.'}${m.dateNotification ? ` Notification proposée le ${fmtDate(m.dateNotification)}.` : ''}`}>
      <span style={{ fontSize: 12.5, color: couleur, fontWeight: m.perime || m.bientotPerime ? 700 : 400 }}>
        {m.phraseEcheance
          ? `${m.phraseEcheance} — ${fmtDate(m.echeanceApresOuverture)}`
          : `jusqu’au ${fmtDate(m.echeanceApresOuverture)} · ${m.causeLibelle}`}
        {m.perime ? ` (depuis ${-j} j)` : m.bientotPerime ? ` (dans ${j} j)` : ''}
        {m.manquesEcheance.length ? ' *' : ''}
      </span>
      {deux}
    </span>
  );
}

export function Materiaux({ onglet: ongletImpose, encastre = false }: {
  /** Imposé par l'enveloppe « Matériel » (M1, 10/10/2026) : l'onglet est alors tenu dehors. */
  onglet?: 'flacons' | 'aliquots';
  /** Rendu DANS « Matériel » : ni titre ni segment propres — l'enveloppe les porte. */
  encastre?: boolean;
} = {}) {
  const { settings } = useGed();
  const [lignes, setLignes] = useState<Materiau[]>([]);
  const [compte, setCompte] = useState<Compte | null>(null);
  const [seuil, setSeuil] = useState(60);
  // Le seuil est-il une DÉCISION du laboratoire, ou une valeur reprise faute de
  // mieux ? Les deux se ressemblent à l'écran, et c'est précisément le piège.
  const [seuilConfirme, setSeuilConfirme] = useState(true);
  const [chargement, setChargement] = useState(true);
  /* L6 (10/10/2026) : un échec de lecture se DIT et se REPREND (règle 8). */
  const [erreurLecture, setErreurLecture] = useState('');
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [peutEcrire, setPeutEcrire] = useState(false);
  const [peutDeclarer, setPeutDeclarer] = useState(false);
  const [version, setVersion] = useState(0);
  const [edition, setEdition] = useState<{ famille: 'calibrant' | 'controle'; ligne: Materiau | null } | null>(null);
  const [archivage, setArchivage] = useState<Materiau | null>(null);
  /* Deux onglets (03/10/2026) : les MATÉRIAUX À RECONSTITUER (le registre
     historique, tel quel) et les ALIQUOTS qui en sont tirés. Un onglet et non
     une rubrique de plus : c'est le même matériau, suivi à deux échelles.
     ⚠️ L'onglet s'intitulait « Flacons » jusqu'au 05/10/2026 (RQ) : le registre
     tient aussi des calibrants et des produits qui n'arrivent pas en flacon,
     et « flacon » reste le mot du CONTENANT — il ne vaut que là où une boîte
     est réellement en jeu (« numéro de flacon », « flacons restants »). La clé
     d'état, elle, ne bouge pas : rien ne la lit en dehors de ce fichier. */
  const [ongletLocal, setOnglet] = useState<'flacons' | 'aliquots'>('flacons');
  const onglet = ongletImpose ?? ongletLocal;

  // `.then()` et non `await` dans l'effet : une fonction `async` appelée
  // directement empêche de voir que les `setState` suivent le premier await.
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/materiaux', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { lignes: [], compte: null }))
      .then((d) => {
        if (!vivant) return;
        setErreurLecture('');
        setLignes(d.lignes || []);
        setCompte(d.compte || null);
        setPeutEcrire(d.peutEcrire === true);
        setPeutDeclarer(d.peutDeclarer === true);
        if (d.seuilJours) setSeuil(d.seuilJours);
        setSeuilConfirme(d.seuilConfirme !== false);
      })
      .catch(() => { if (vivant) setErreurLecture('Le registre des matériaux n’a pas pu être lu : le serveur n’a pas répondu.'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [version]);

  const filtrees = useMemo(() => {
    // Les archivées ne se voient QUE par leur pastille dédiée.
    const vivantes = lignes.filter((m) => !m.archive);
    /* Une TABLE plutôt qu'une échelle de ternaires : à sept branches elle ne
       se relisait plus, et son indentation mentait sur l'imbrication. Une clé
       inconnue rend la liste entière — jamais une liste vide, qui se lirait
       « aucun réactif » alors que c'est le filtre qui est faux. */
    const TRIS: Record<string, (m: Materiau) => boolean> = {
      calibrant: (m) => m.famille === 'calibrant',
      controle: (m) => m.famille === 'controle',
      prets: (m) => !m.seReconstitue,
      sanstemp: (m) => !m.temperatureAvant && !m.temperatureApres,
      perimes: (m) => m.perime,
      bientot: (m) => m.bientotPerime,
      inconnue: (m) => m.peremptionInconnue,
    };
    const base = filtre === 'archives'
      ? lignes.filter((m) => m.archive)
      : vivantes.filter(TRIS[filtre] || (() => true));
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((m) => `${m.libelle} ${m.numeroLot} ${m.codeBoxystock} ${m.discipline} ${m.equipementCode}`.toLowerCase().includes(qq))
      : base;
  }, [lignes, filtre, q]);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: encastre ? 'flex-end' : 'space-between', gap: 14, flexWrap: 'wrap' }}>
        {!encastre && <H1 eyebrow={<>Ressources · <RefISO code="6.6" /></>} title="Réactifs de contrôle et de calibration" />}
        {peutDeclarer && (
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <Bouton variante="primaire" taille="sm" onClick={() => setEdition({ famille: 'calibrant', ligne: null })}>
              <I n="plus" s={13} />Calibrant reconstitué
            </Bouton>
            <Bouton variante="primaire" taille="sm" onClick={() => setEdition({ famille: 'controle', ligne: null })}>
              <I n="plus" s={13} />Contrôle reconstitué
            </Bouton>
          </span>
        )}
      </div>
      {!encastre && (
      <div role="tablist" aria-label="Échelle de suivi" style={{ display: 'flex', gap: 6 }}>
        <Segment ariaLabel="Section" valeur={onglet} onChange={setOnglet} options={([['flacons', 'Réactifs'], ['aliquots', 'Aliquots de contrôle']] as const).map(([k, l]) => ({ valeur: k, libelle: l }))} />
      </div>
      )}
      {onglet === 'aliquots' && <Aliquots />}
      {onglet === 'flacons' && <>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        Calibrants et matériaux de contrôle, suivis unité par unité. Ce sont deux choses
        distinctes — l’un règle la mesure, l’autre la vérifie — mais le suivi est le même.
        Tous ne se reconstituent pas : un réactif prêt à l’emploi se range ici comme les
        autres, et sa fiche ne réclame alors aucune date de reconstitution.
      </p>

      {/* UNE alerte de péremption, sur la PREMIÈRE date échue, ventilée par
          cause (RQ, 03/10/2026) : « fin du lot » appelle un autre lot, « fin de
          stabilité » un flacon reconstitué. Jusqu'au 03/10 il y en avait deux
          (kit fermé / validité après ouverture) ; elles comptaient désormais
          la même chose. */}
      {compte && (compte.perimes > 0 || compte.epuises > 0) && (
        <div style={{ background: 'var(--red-50)', borderInlineStart: '3px solid var(--red-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          <strong style={{ color: 'var(--red-600)' }}>
            {compte.perimes > 0 && `${compte.perimes} matériau${compte.perimes > 1 ? 'x' : ''} périmé${compte.perimes > 1 ? 's' : ''}`}
            {compte.perimes > 0 && ` (${[
              compte.perimesParCause?.lot ? `${compte.perimesParCause.lot} : fin du lot` : '',
              compte.perimesParCause?.stabilite ? `${compte.perimesParCause.stabilite} : fin de stabilité` : '',
            ].filter(Boolean).join(', ')})`}
            {compte.perimes > 0 && compte.epuises > 0 && ' · '}
            {compte.epuises > 0 && `${compte.epuises} épuisé${compte.epuises > 1 ? 's' : ''}`}
          </strong>{' '}
          — l’outil le signale, il ne bloque rien : c’est au poste de juger.
        </div>
      )}
      {compte && compte.bientotPerimes > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {`${compte.bientotPerimes} matériau${compte.bientotPerimes > 1 ? 'x' : ''} bientôt périmé${compte.bientotPerimes > 1 ? 's' : ''} (sous ${seuil} j`}
            {[
              compte.bientotPerimesParCause?.lot ? ` — ${compte.bientotPerimesParCause.lot} : fin du lot` : '',
              compte.bientotPerimesParCause?.stabilite ? ` — ${compte.bientotPerimesParCause.stabilite} : fin de stabilité` : '',
            ].join('')})
          </strong>{' '}
          — l’échéance est la première de la péremption du lot et de la péremption après
          reconstitution. L’outil le signale, il ne bloque rien.
        </div>
      )}

      {!seuilConfirme && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>Le délai d’avertissement de {seuil} jours n’a pas été validé par le laboratoire.</strong>{' '}
          Il est repris de celui des lots de réactifs, pour éviter deux notions de « bientôt
          périmé » dans le même outil. Il dit seulement <em>combien de jours à l’avance
          prévenir</em> — jamais combien de temps un produit tient, qui se règle produit par
          produit. À fixer dans le paramétrage Qualité, rubrique <strong>Validité des
          matériaux</strong> ; ce bandeau disparaîtra alors.
        </div>
      )}

      {compte && compte.peremptionInconnue > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          <strong style={{ color: 'var(--text-strong)' }}>{compte.peremptionInconnue} matériau{compte.peremptionInconnue > 1 ? 'x' : ''} sans date de péremption lisible.</strong>{' '}
          Ni la péremption du lot ni celle après reconstitution ne sont saisies : ils ne sont ni
          valides ni périmés, on ne sait pas. Ils n’apparaissent dans aucune des alertes ci-dessus.
        </div>
      )}

      {chargement && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</p>}

      {!chargement && compte && (
        <>
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher un matériau, un lot, un code stock…"
            chips={[
              { k: 'tous', label: 'Tous', n: compte.total },
              { k: 'calibrant', label: 'Calibrants', n: compte.calibrants },
              { k: 'controle', label: 'Contrôles CQI', n: compte.controles },
              /* La pastille existe même à zéro, et c'est voulu : tant que
                 personne n'a tranché produit par produit, « 0 prêt à l'emploi »
                 veut dire « rien n'a été renseigné », pas « il n'y en a
                 aucun ». La montrer, c'est rendre la lacune visible. */
              { k: 'prets', label: 'Prêts à l’emploi', n: compte.pretsALEmploi ?? 0 },
              { k: 'sanstemp', label: 'Sans température', n: compte.sansTemperature ?? 0 },
              { k: 'perimes', label: 'Périmés', n: compte.perimes },
              { k: 'bientot', label: `Périment sous ${seuil} j${seuilConfirme ? '' : ' *'}`, n: compte.bientotPerimes },
              { k: 'inconnue', label: 'Péremption inconnue', n: compte.peremptionInconnue },
              ...(compte.archives ? [{ k: 'archives', label: 'Archivées', n: compte.archives }] : []),
            ]}
            actif={filtre} onChip={setFiltre}
            total={compte.total} affiches={filtrees.length}
            onReset={() => { setQ(''); setFiltre('tous'); }} />

          {/* ── UN TABLEAU, AVEC DES EN-TÊTES (RQ, 05/10/2026) ──────────────
              La liste alignait jusqu'à douze valeurs par ligne dans une rangée
              souple, chacune reconnaissable à son seul préfixe (« lot … »,
              « flacon … », « ouvert le … ») et ABSENTE quand elle était vide :
              deux lignes voisines ne mettaient pas la même donnée à la même
              place, et rien ne nommait ce qu'on lisait. Le tableau commun
              apporte les trois choses qui manquaient : des intitulés, des
              colonnes alignées, et le TRI (par échéance, par lot, par
              matériau) — jusqu'ici il fallait parcourir la liste entière pour
              trouver ce qui périme le premier. */}
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {(
              <Tableau
                id="materiaux-reconstitues"
                ajusterLargeur
                /* L6 (10/10/2026) : niveau STANDARD — filtres (famille, niveau,
                   équipement en liste ; reconstitué le en dates), masquage, états
                   intégrés (squelette, erreur + Réessayer, vide), gestes RQ en
                   colonne d'actions. */
                niveau="standard"
                chargement={chargement}
                erreur={erreurLecture || null}
                onReessayer={() => { setChargement(true); setVersion((n) => n + 1); }}
                vide={{
                  titre: lignes.length === 0 ? 'Aucun matériau enregistré.' : 'Aucun matériau ne correspond à ces critères.',
                  indice: lignes.length === 0 ? 'Les calibrants et contrôles reconstitués apparaîtront ici.' : 'Élargissez la recherche ou revenez à « Tous ».',
                  action: lignes.length > 0
                    ? <Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser les filtres</Bouton>
                    : undefined,
                }}
                lignes={filtrees}
                cleLigne={(m) => m.documentId}
                ligneProps={(m) => (m.archive ? { style: { opacity: 0.65 } } : {})}
                colonnes={[
                  {
                    cle: 'date', libelle: 'Reconstitué le', type: 'date',
                    valeur: (m) => m.dateReconstitution || '',
                    style: { ...mono, width: 112 },
                    /* « sans objet » et « — » ne disent PAS la même chose : le
                       premier est une propriété du produit (il ne se
                       reconstitue pas), le second une lacune de saisie. Les
                       confondre ferait réclamer une date qui n'existe pas, ou
                       passer une lacune pour une normalité. */
                    rendu: (m) => (m.dateReconstitution
                      ? <>{fmtDate(m.dateReconstitution)}</>
                      : !m.seReconstitue
                        ? (
                          <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontStyle: 'italic' }}
                            title="Réactif prêt à l’emploi : il n’y a pas de reconstitution à dater.">sans objet</span>
                        )
                        : <V v={null} />),
                  },
                  {
                    cle: 'famille', libelle: 'Famille', type: 'liste',
                    valeur: (m) => FAMILLES[m.famille]?.label || m.famille,
                    style: { width: 106 },
                    rendu: (m) => (
                      <>
                        <span title={FAMILLES[m.famille]?.aide}
                          style={{ fontWeight: 700, color: 'var(--text-muted)', cursor: 'help' }}>
                          {FAMILLES[m.famille]?.label || m.famille}
                        </span>
                        {!m.seReconstitue && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontStyle: 'italic' }}
                            title="Ce réactif ne passe pas par une reconstitution.">prêt à l’emploi</div>
                        )}
                      </>
                    ),
                  },
                  {
                    cle: 'libelle', libelle: 'Matériau', valeur: (m) => m.libelle,
                    // La seule colonne qui s'autorise deux lignes : c'est elle
                    // qui absorbe la largeur restante, les autres étant courtes.
                    style: { whiteSpace: 'normal', minWidth: 180 },
                    rendu: (m) => (
                      <>
                        <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{m.libelle}</span>
                        {m.epuise && <> <span style={{ fontWeight: 700, color: 'var(--red-600)' }}>épuisé</span></>}
                        {m.archive && <> <BadgeArchive motif={m.archiveMotif} /></>}
                      </>
                    ),
                  },
                  {
                    cle: 'niveau', libelle: 'Niveau', type: 'liste', valeur: (m) => m.niveau || '',
                    style: { width: 76 }, rendu: (m) => <V v={m.niveau} />,
                  },
                  {
                    cle: 'equipement', libelle: 'Équipement', type: 'liste', valeur: (m) => m.equipementCode || '',
                    style: { ...mono, width: 118 }, rendu: (m) => <V v={m.equipementCode} />,
                  },
                  {
                    cle: 'lot', libelle: 'Lot', valeur: (m) => m.numeroLot || '',
                    style: { ...mono }, rendu: (m) => <V v={m.numeroLot} />,
                  },
                  {
                    cle: 'flacon',
                    libelle: <span title="Le contenant lui-même : son numéro, et le volume d’une unité quand il est connu.">Flacon</span>,
                    valeur: (m) => m.numeroFlacon || '',
                    rendu: (m) => (m.numeroFlacon || m.volumeParUnite
                      ? (
                        <>
                          {m.numeroFlacon && <span>{m.numeroFlacon}</span>}
                          {m.volumeParUnite && (
                            <span style={{ color: 'var(--text-muted)' }}>{m.numeroFlacon ? ' · ' : ''}{m.volumeParUnite}</span>
                          )}
                        </>
                      )
                      : <V v={null} />),
                  },
                  {
                    cle: 'ouverture',
                    /* La date d'ouverture du kit n'existe QUE sur le registre
                       des contrôles ; côté calibrants la colonne reste vide, et
                       c'est un écart signalé au laboratoire, pas un oubli
                       d'affichage — l'infobulle le dit plutôt que de laisser
                       croire que personne n'ouvre les calibrants. */
                    libelle: <span title="Rubrique du registre des contrôles seulement : les calibrants n’en portent pas — un écart signalé au laboratoire, pas un oubli d’affichage.">Ouvert le</span>,
                    valeur: (m) => m.dateOuverture || '',
                    style: { ...mono, width: 112 },
                    rendu: (m) => (m.dateOuverture ? <>{fmtDate(m.dateOuverture)}</> : <V v={null} />),
                  },
                  {
                    cle: 'echeance', libelle: 'Échéance',
                    /* Tri sur les JOURS RESTANTS et non sur la date affichée :
                       c'est la question du poste (« qu'est-ce qui périme le
                       premier ? »). Les péremptions inconnues valent `null` et
                       partent donc en queue dans les deux sens — ce ne sont ni
                       les plus proches ni les plus lointaines, on ne sait pas. */
                    valeur: (m) => (m.peremptionInconnue ? null : m.joursAvantPeremption),
                    style: { whiteSpace: 'normal', minWidth: 230 },
                    rendu: (m) => <Echeance m={m} />,
                  },
                  {
                    cle: 'conservation', libelle: 'Conservation',
                    valeur: (m) => m.temperatureApresDite || m.temperatureAvantDite || '',
                    style: { whiteSpace: 'normal', minWidth: 120 },
                    /* La température de la NOTICE, et celle des aliquots
                       seulement quand elle en diffère : l'afficher toujours
                       doublerait la colonne pour ne rien apprendre, ne jamais
                       l'afficher cacherait l'écart. L'écart est signalé en
                       ambre — il n'est PAS une faute (aliquoter et congeler un
                       contrôle rangé à 2 – 8 °C est la pratique normale), il
                       est une chose à pouvoir justifier. */
                    rendu: (m) => (!m.temperatureApresDite && !m.temperatureAvantDite && !m.temperatureAliquotsDite
                      ? <V v={null} />
                      : (
                        <>
                          {m.temperatureApresDite || m.temperatureAvantDite || ''}
                          {m.ecartConservation && (
                            <div style={{ fontSize: 11.5, color: 'var(--amber-700)' }} title={m.ecartConservation}>
                              aliquots : {m.temperatureAliquotsDite}
                            </div>
                          )}
                        </>
                      )),
                  },
                  {
                    /* La colonne porte la CLÉ TECHNIQUE (`coord.ezzi`) : on
                       nomme la personne, au tri comme à l'écran (11/10/2026). */
                    cle: 'personnel', libelle: 'Personnel',
                    valeur: (m) => (m.personnel ? userName(settings, m.personnel) : ''),
                    rendu: (m) => <V v={m.personnel ? userName(settings, m.personnel) : m.personnel} />,
                  },
                ]}
                actions={peutEcrire ? (m) => (
                  <ActionsLigne archive={m.archive}
                    onCorriger={() => setEdition({ famille: m.famille as 'calibrant' | 'controle', ligne: m })}
                    onArchiver={() => setArchivage(m)} />
                ) : undefined}
              />
            )}
          </div>
        </>
      )}

      </>}

      {edition && (
        <FormulaireLigne
          chemin={CHEMINS[edition.famille]}
          titreCreation={edition.famille === 'calibrant' ? 'Déclarer un calibrant reconstitué' : 'Déclarer un contrôle reconstitué'}
          champs={edition.famille === 'calibrant' ? CALIBRANT_CHAMPS : FLACON_CHAMPS}
          ligne={edition.ligne ? versBrut(edition.ligne) : null}
          onClose={() => setEdition(null)} onDone={() => setVersion((n) => n + 1)} />
      )}
      {archivage && (
        <ArchiverModale
          chemin={CHEMINS[archivage.famille as 'calibrant' | 'controle']}
          ligne={archivage}
          refLigne={`${FAMILLES[archivage.famille]?.label || archivage.famille} — ${archivage.libelle}${archivage.numeroLot ? ` (lot ${archivage.numeroLot})` : ''}`}
          onClose={() => setArchivage(null)} onDone={() => setVersion((n) => n + 1)} />
      )}
    </div>
  );
}
